import React, { memo, useCallback, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@context/themeContext';
import { themeColors } from '@styles/theme';
import { useGroupSelectionContext } from '@context/groupSelectionContext';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { SelectedFeatureProperties } from '@redux/showInfo/types';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import Modal from '@components/Modal/Modal';
import { CreateSelectionSetIcon } from '@icons/group-selection/CreateSelectionSetIcon';
import GroupSelectionSetsMergeForm from './GroupSelectionSetsMergeForm';

type GroupSelectionSetsCreateActionProps = {
    selectedFeatures: SelectedFeatureProperties[];
    onSave: (id: string) => void;
};

const GroupSelectionSetsCreateAction = memo(
    ({ selectedFeatures, onSave }: GroupSelectionSetsCreateActionProps) => {
        const [name, setName] = useState('');
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isCreating, setIsCreating] = useState(false);
        const { createGroupSelectionSet } = useGroupSelectionContext();
        const { addToast } = useToastContext();
        const { t } = useTranslation();
        const { theme } = useTheme();

        const disabledBtn = !selectedFeatures.length || isCreating;

        const onSetNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
        }, []);

        const onFormSubmit = useCallback(
            async (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formName = String(formData.get('name') ?? '').trim();
                if (!formName) {
                    return;
                }

                setIsCreating(true);
                const resp = await createGroupSelectionSet(
                    formName,
                    selectedFeatures.map(({ id }) => id)
                );
                if (isErrorResponse(resp)) {
                    try {
                        const parsedErr =
                            typeof resp.error === 'string'
                                ? safelyParseJson(resp.error)?.error
                                : resp.error.error;
                        addToast(parsedErr ?? t('common.unknown-error'), 'error');
                    } catch (e) {
                        console.log('Replan. Error parsing error response', resp, e);
                    }
                } else {
                    addToast(t('common.save-successful'), 'success');
                    setName('');
                    setIsModalOpen(false);
                    if (resp.id) {
                        onSave(resp.id);
                    }
                }
                setIsCreating(false);
            },
            [createGroupSelectionSet, selectedFeatures, addToast, t, onSave]
        );

        return (
            <>
                <Tooltip title={t('road-network.group-selection-create-selection-set-btn')}>
                    <div>
                        <IconButton
                            icon={() => (
                                <CreateSelectionSetIcon
                                    color={
                                        disabledBtn
                                            ? themeColors.grey3
                                            : theme === 'light'
                                              ? themeColors.bluePurple
                                              : themeColors.white
                                    }
                                />
                            )}
                            width={24}
                            height={24}
                            borderless
                            onClick={() => setIsModalOpen(true)}
                            disabled={disabledBtn}
                        />
                    </div>
                </Tooltip>
                {isModalOpen ? (
                    <Modal
                        title={t('road-network.save-route-as-selection-set.modal-title')}
                        setVisible={setIsModalOpen}
                        narrow
                    >
                        <GroupSelectionSetsMergeForm
                            setName={name}
                            onSetNameChange={onSetNameChange}
                            onFormSubmit={onFormSubmit}
                            disabled={isCreating || !name.trim()}
                        />
                    </Modal>
                ) : null}
            </>
        );
    }
);

export default GroupSelectionSetsCreateAction;
